/**
 * The work programme: what to do, when, and what could not be decided.
 *
 * Every other screen here answers one question. This one composes four —
 * money, traffic, weather and crew — into a sequence of jobs with dates. That
 * part is ordinary; asset-management systems have produced priority lists for
 * thirty years.
 *
 * WHAT IS NOT ORDINARY is the third bucket. Every planning tool in this market
 * emits a complete-looking plan, because a plan with holes in it demonstrates
 * badly. It is nevertheless the only kind that survives an estate whose data is
 * half-loaded, which is every estate. A programme that silently drops the six
 * candidates nobody priced has not scheduled them — it has hidden them, and
 * they surface later as emergencies.
 *
 * So `undecidable` has the same standing as `scheduled`, every row names ONE
 * missing fact and the desk that supplies it, and the work held up that way is
 * counted IN MONEY — because "our data is incomplete" is not something anybody
 * funds, and "SAR 110,400 of funded work is held up by two missing position
 * codes" is.
 */

export interface ScheduledJob {
  id: string;
  title: string;
  subject?: string;
  cost: number;
  escalationAvoided?: number;
  from: string;
  to: string;
  whyThen: string;
  weatherBasis?: string[];
}

export interface DeferredJob {
  id: string;
  title: string;
  costNow?: number;
  escalation?: number;
  why: string;
}

/** A decision the platform declined to make, and the one fact that would move it. */
export interface UndecidableJob {
  id: string;
  title: string;
  /** Null when the candidate could not even be priced. */
  costNow?: number;
  missing: string;
  whoSupplies: string;
  unblocks: string;
}

export interface Programme {
  available: boolean;
  whyNot?: string;
  fiscalYear: number;
  currency?: string;
  envelope?: number;
  searched?: { from: string; to: string; days: number };
  scheduled: ScheduledJob[];
  deferred: DeferredJob[];
  undecidable: UndecidableJob[];
  totals: {
    scheduled: number;
    scheduledCost: number;
    deferred: number;
    deferredEscalation: number;
    undecidable: number;
    envelopeUnspent: number;
    /** Priced, funded work held up by a missing fact rather than by money. */
    blockedByMissingFacts: number;
  };
  rankedBy?: string;
  note?: string;
  horizonNote?: string;
}

/**
 * How much of the register the programme could reason about at all.
 *
 * Returned as a fraction of candidates that reached a decision — scheduled or
 * deferred — over the whole register. It is the honest headline: a programme
 * that decided two of eight is not an 8-job plan with six footnotes.
 */
export function decidedFraction(p: Programme): number | null {
  const total = p.scheduled.length + p.deferred.length + p.undecidable.length;
  if (!total) return null;
  return (p.scheduled.length + p.deferred.length) / total;
}

/**
 * Group the undecidable rows by the desk that would unblock them.
 *
 * This is what turns the register into an action. A list of eleven missing
 * facts is a complaint; "commercial owes you four rates, worth 340,000 of
 * schedulable work" is a task with an owner.
 */
export function byDesk(rows: UndecidableJob[]): { desk: string; rows: UndecidableJob[]; worth: number }[] {
  const map = new Map<string, UndecidableJob[]>();
  for (const r of rows) {
    const k = r.whoSupplies || "unassigned";
    map.set(k, [...(map.get(k) ?? []), r]);
  }
  return [...map.entries()]
    .map(([desk, rs]) => ({
      desk,
      rows: rs,
      worth: rs.reduce((t, r) => t + (r.costNow ?? 0), 0),
    }))
    /* by money first: the desk holding up the most funded work is the one to
       call, and it is often not the one holding up the most rows */
    .sort((a, b) => b.worth - a.worth || b.rows.length - a.rows.length);
}
